import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Button, Input, Select } from "@/components/ui"
import { fehlerNachricht, useToast } from "@/lib/toast"
import {
  addErsatzteilKomponente,
  createErsatzteil,
  deleteErsatzteil,
  fetchErsatzteil,
  fetchErsatzteilarten,
  fetchErsatzteilKomponenten,
  fetchErsatzteilLieferanten,
  updateErsatzteil,
  type ErsatzteilEingabe,
} from "@/lib/api"

const FOCUS_LINK_CLASS =
  "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"

const LEER: ErsatzteilEingabe = {
  bezeichnung: "",
  beschreibung: null,
  ersatzteilartId: null,
  lieferant1OrganisationId: null,
  lieferant2OrganisationId: null,
  lieferant3OrganisationId: null,
  spsZuordnung: null,
  kommunikationsprotokoll: null,
  messgroesse: null,
  ersatzWirdVerwendet: false,
}

/**
 * Ersatzteil-Detailformular (AP-3.5) — bis 3 Lieferanten via Stammdaten-Client.
 *
 * Route-Wrapper (Deep-Link-Fallback, `/maschinen/ersatzteile/:id` bzw. `/neu`) —
 * liest die ID aus der URL und delegiert an `ErsatzteilDetailContent`. Primärer
 * Aufrufweg ist das Sheet aus `ErsatzteilePage` (UX_KONZEPT.md §7).
 */
export function ErsatzteilDetailPage() {
  const params = useParams<{ id: string }>()
  const istNeu = params.id === "neu"
  const ersatzteilId = istNeu ? null : Number(params.id)
  const navigate = useNavigate()
  // Issue #5: „+ Neu" aus der Komponentenansicht —
  // ?komponente=<id> ordnet das neue Ersatzteil der Komponente zu,
  // ?zurueck=<pfad> führt nach dem Speichern dorthin zurück
  const [searchParams] = useSearchParams()
  const komponenteParam = searchParams.get("komponente")
  const zurueck = searchParams.get("zurueck")
  return (
    <ErsatzteilDetailContent
      ersatzteilId={ersatzteilId}
      onGespeichert={(ergebnis) => {
        if (!istNeu) return
        void (async () => {
          if (komponenteParam) {
            try {
              await addErsatzteilKomponente(ergebnis.id, Number(komponenteParam))
            } catch {
              // Zuordnung fehlgeschlagen: Ersatzteil existiert trotzdem —
              // Detailseite öffnen, Zuordnung dort manuell möglich
              navigate(`/maschinen/ersatzteile/${ergebnis.id}`)
              return
            }
          }
          navigate(zurueck ?? `/maschinen/ersatzteile/${ergebnis.id}`)
        })()
      }}
      onGeloescht={() => navigate("/maschinen/ersatzteile")}
    />
  )
}

/** Inhalt der Ersatzteil-Detailansicht — wird von der Route (Deep-Link-Fallback)
 * und vom Sheet (`ErsatzteilePage`) genutzt. */
export function ErsatzteilDetailContent({
  ersatzteilId,
  onGespeichert,
  onGeloescht,
}: {
  ersatzteilId: number | null
  onGespeichert?: (ergebnis: { id: number }) => void
  onGeloescht?: () => void
}) {
  const istNeu = ersatzteilId === null
  const queryClient = useQueryClient()
  const toast = useToast()

  const [form, setForm] = useState<ErsatzteilEingabe>(LEER)

  const { data: ersatzteil } = useQuery({
    queryKey: ["ersatzteile", ersatzteilId],
    queryFn: () => fetchErsatzteil(ersatzteilId!),
    enabled: ersatzteilId !== null,
  })
  const { data: arten } = useQuery({ queryKey: ["ersatzteilarten"], queryFn: fetchErsatzteilarten })
  const { data: lieferanten } = useQuery({
    queryKey: ["ersatzteile", ersatzteilId, "lieferanten"],
    queryFn: () => fetchErsatzteilLieferanten(ersatzteilId!),
    enabled: ersatzteilId !== null,
  })
  const { data: komponenten } = useQuery({
    queryKey: ["ersatzteile", ersatzteilId, "komponenten"],
    queryFn: () => fetchErsatzteilKomponenten(ersatzteilId!),
    enabled: ersatzteilId !== null,
  })

  useEffect(() => {
    if (ersatzteil) setForm(ersatzteil)
  }, [ersatzteil])

  const speichern = useMutation({
    mutationFn: () =>
      istNeu ? createErsatzteil(form) : updateErsatzteil(ersatzteilId!, form),
    onSuccess: (ergebnis) => {
      queryClient.invalidateQueries({ queryKey: ["ersatzteile"] })
      toast.success(istNeu ? "Ersatzteil angelegt." : "Ersatzteil gespeichert.")
      onGespeichert?.(ergebnis)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Ersatzteil konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deleteErsatzteil(ersatzteilId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ersatzteile"] })
      toast.success("Ersatzteil gelöscht.")
      onGeloescht?.()
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Ersatzteil konnte nicht gelöscht werden.")),
  })

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {istNeu ? "Neues Ersatzteil" : ersatzteil?.bezeichnung ?? "Ersatzteil"}
        </h1>
        <p className="mt-1 text-sm text-slate-600">02-L — bis zu 3 Lieferanten (§7.2).</p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-medium text-slate-900">Stammdaten</h2>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              speichern.mutate()
            }}
          >
            <label className="block text-sm">
              Bezeichnung
              <Input
                required
                value={form.bezeichnung}
                onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
                className="mt-1"
              />
            </label>
            <label className="block text-sm">
              Ersatzteilart
              <Select
                value={form.ersatzteilartId ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    ersatzteilartId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="mt-1"
              >
                <option value="">—</option>
                {arten?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </label>
            <label className="block text-sm">
              SPS-Zuordnung
              <Input
                value={form.spsZuordnung ?? ""}
                onChange={(e) => setForm({ ...form, spsZuordnung: e.target.value })}
                className="mt-1"
              />
            </label>
            <label className="block text-sm">
              Kommunikationsprotokoll
              <Input
                value={form.kommunikationsprotokoll ?? ""}
                onChange={(e) => setForm({ ...form, kommunikationsprotokoll: e.target.value })}
                className="mt-1"
              />
            </label>
            <label className="block text-sm">
              Messgröße
              <Input
                value={form.messgroesse ?? ""}
                onChange={(e) => setForm({ ...form, messgroesse: e.target.value })}
                className="mt-1"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.ersatzWirdVerwendet}
                onChange={(e) => setForm({ ...form, ersatzWirdVerwendet: e.target.checked })}
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              />
              Ersatz wird verwendet
            </label>

            <fieldset className="space-y-2 rounded-md bg-slate-50 p-3">
              <legend className="px-1 text-xs font-medium text-slate-500">
                Lieferanten (Stammdaten-Organisation-IDs, bis zu 3)
              </legend>
              <Input
                placeholder="Lieferant 1 — Organisation-ID"
                value={form.lieferant1OrganisationId ?? ""}
                onChange={(e) => setForm({ ...form, lieferant1OrganisationId: e.target.value || null })}
              />
              <Input
                placeholder="Lieferant 2 — Organisation-ID"
                value={form.lieferant2OrganisationId ?? ""}
                onChange={(e) => setForm({ ...form, lieferant2OrganisationId: e.target.value || null })}
              />
              <Input
                placeholder="Lieferant 3 — Organisation-ID"
                value={form.lieferant3OrganisationId ?? ""}
                onChange={(e) => setForm({ ...form, lieferant3OrganisationId: e.target.value || null })}
              />
            </fieldset>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" variant="primary" disabled={speichern.isPending}>
                Speichern
              </Button>
              {!istNeu && (
                <Button type="button" variant="destructive" onClick={() => loeschen.mutate()}>
                  Löschen
                </Button>
              )}
            </div>
          </form>
        </section>

        {!istNeu && (
          <section className="rounded-lg border border-slate-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-medium text-slate-900">
              Lieferanten (aus rmnc-stammdaten)
            </h2>
            <ul className="space-y-2 text-sm">
              {lieferanten?.map((l) => (
                <li key={l.id} className="flex justify-between rounded-md bg-slate-50 px-3 py-2">
                  <span>{l.name}</span>
                  <span className="text-slate-500">{l.kuerzel ?? l.id}</span>
                </li>
              ))}
              {lieferanten?.length === 0 && (
                <li className="text-slate-400">Keine Lieferanten hinterlegt oder auflösbar.</li>
              )}
            </ul>
          </section>
        )}

        {!istNeu && (
          <section className="rounded-lg border border-slate-200 bg-white p-6 lg:col-span-2">
            <h2 className="mb-4 text-lg font-medium text-slate-900">
              Verbaut in ({komponenten?.length ?? 0} Komponenten)
            </h2>
            <ul className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {komponenten?.map((k) => (
                <li key={k.id} className="rounded-md bg-slate-50 px-3 py-2">
                  <Link to={`/maschinen/komponenten/${k.id}`} className={`hover:underline ${FOCUS_LINK_CLASS}`}>
                    {k.beschreibung}
                  </Link>
                  {k.tagNummer && (
                    <span className="ml-2 font-mono text-xs text-slate-500">{k.tagNummer}</span>
                  )}
                </li>
              ))}
              {komponenten?.length === 0 && (
                <li className="text-slate-400">In keiner Komponente verbaut (02-C.P2).</li>
              )}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}
